"use client";

import * as React from "react";
import { useReducedMotion } from "motion/react";
import { cn } from "@marketing-sites/utils/cn";
import { MarqueeContext, useMarquee } from "./marquee.context";
import { useMarqueeDuration } from "./marquee.hooks";
import type { RootProps } from "./marquee.types";

/** Root context provider. Measures the track, manages hover-pause state and provides animation config to children. */
export function Root({
  speed = 40,
  direction = "left",
  pauseOnHover = false,
  staticWhenFits = false,
  fadeLeft = true,
  fadeRight = true,
  children,
  className,
}: RootProps) {
  const trackRef = React.useRef<HTMLDivElement>(null);
  const [isHovering, setIsHovering] = React.useState(false);
  const prefersReducedMotion = useReducedMotion();

  const { fits, duration, numCopies } = useMarqueeDuration(
    trackRef,
    speed,
    staticWhenFits,
  );

  const isPaused = pauseOnHover && isHovering;
  const isStatic = staticWhenFits && (fits || Boolean(prefersReducedMotion));

  const fade =
    fadeLeft && fadeRight
      ? "both"
      : fadeLeft
        ? "left"
        : fadeRight
          ? "right"
          : undefined;

  return (
    <MarqueeContext.Provider
      value={{
        trackRef,
        speed,
        direction,
        isPaused,
        isStatic,
        fits,
        duration,
        numCopies,
      }}
    >
      <div
        data-marquee-root=""
        data-marquee-fade={fade}
        data-marquee-fits={isStatic && fits ? "" : undefined}
        className={cn(
          isStatic && !fits ? "overflow-x-auto" : "overflow-hidden",
          className,
        )}
        onMouseEnter={pauseOnHover ? () => setIsHovering(true) : undefined}
        onMouseLeave={pauseOnHover ? () => setIsHovering(false) : undefined}
      >
        {children}
      </div>
    </MarqueeContext.Provider>
  );
}

/** Scrolling track. Pass `gap-*` via `className` to control spacing between items and at the loop seam. */
export function Track({
  className,
  children,
  ...rest
}: React.ComponentProps<"div">) {
  const {
    trackRef,
    speed,
    direction,
    isPaused,
    isStatic,
    fits,
    duration,
    numCopies,
  } = useMarquee();

  return (
    <div
      ref={trackRef}
      data-marquee-track=""
      className={cn(
        "flex",
        isStatic && fits ? "w-full justify-center" : "w-max",
        className,
      )}
      style={
        {
          "--marquee-duration": `${duration}s`,
          "--marquee-translate": `-${duration * speed}px`,
          animationName: isStatic
            ? "none"
            : direction === "left"
              ? "marquee-left"
              : "marquee-right",
          animationDuration: "var(--marquee-duration)",
          animationTimingFunction: "linear",
          animationIterationCount: "infinite",
          animationPlayState: isPaused || duration === 0 ? "paused" : "running",
        } as React.CSSProperties
      }
      {...rest}
    >
      <div className={cn("flex shrink-0", className)}>{children}</div>
      {isStatic
        ? null
        : Array.from({ length: numCopies - 1 }, (_, i) => (
            <div key={i} className={cn("flex shrink-0", className)} aria-hidden>
              {children}
            </div>
          ))}
    </div>
  );
}

/** A single item in the marquee track. */
export function Item({ className, ...rest }: React.ComponentProps<"div">) {
  return <div className={cn("shrink-0", className)} {...rest} />;
}
